import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatDividerModule } from '@angular/material/divider'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { EstimateBalance } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

import {
  BalanceSendChannel,
  BalanceSummaryCapabilities,
  BalanceSummaryData,
  EMPTY_BALANCE_SUMMARY_DATA,
  NO_BALANCE_SUMMARY_CAPABILITIES,
} from '../balance-summary.types'

@Component({
  selector: 'mmx-balance-actions-toolbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
  ],
  templateUrl: './balance-actions-toolbar.component.html',
  styleUrls: ['./balance-actions-toolbar.component.scss'],
})
export class BalanceActionsToolbarComponent {
  @Input() capabilities: BalanceSummaryCapabilities = NO_BALANCE_SUMMARY_CAPABILITIES
  @Input() data: BalanceSummaryData = EMPTY_BALANCE_SUMMARY_DATA
  @Input() busy = false
  @Input() needsPricing = false

  @Output() readonly save = new EventEmitter<void>()
  @Output() readonly revert = new EventEmitter<void>()
  @Output() readonly delete = new EventEmitter<void>()
  @Output() readonly send = new EventEmitter<BalanceSendChannel>()
  @Output() readonly sendDocument = new EventEmitter<void>()
  @Output() readonly viewHistory = new EventEmitter<void>()
  @Output() readonly printLetter = new EventEmitter<EstimateBalance>()

  get hasAnyAction(): boolean {
    const c = this.capabilities
    return !!(c.canViewHistory || c.canDelete || c.canSave || c.canRevert
      || (c.canPrintLetter && this.data.balance) || c.canSend)
  }

  get hasNonDestructiveAction(): boolean {
    const c = this.capabilities
    return !!(c.canViewHistory || c.canSave || c.canRevert
      || (c.canPrintLetter && this.data.balance) || c.canSend)
  }

  emit(action: 'save' | 'revert' | 'delete' | 'viewHistory'): void {
    if (this.busy) {
      return
    }
    const emitter = {
      save: this.save,
      revert: this.revert,
      delete: this.delete,
      viewHistory: this.viewHistory,
    }[action]
    emitter.emit()
  }

  emitSend(channel: BalanceSendChannel): void {
    if (this.busy) {
      return
    }
    this.send.emit(channel)
  }

  emitSendDocument(): void {
    if (this.busy) {
      return
    }
    this.sendDocument.emit()
  }

  emitPrintLetter(): void {
    if (this.busy || !this.data.balance) {
      return
    }
    this.printLetter.emit(this.data.balance)
  }
}
